/**
 * /auth/callback — OIDC redirect target for Hive SSO.
 *
 * Exchanges the authorization code (PKCE) for tokens, then resumes the path
 * captured at beginLogin. Registered redirect URI:
 * http://localhost:5175/auth/callback (dev).
 */
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { Card } from '@/components/ui/card';
import { handleCallback, isHiveMode } from '@/lib/hive';

export function HiveCallbackPage() {
  const [error, setError] = useState<string | null>(null);
  // The code exchange is one-shot (auth codes are single-use) — guard against
  // React 18 StrictMode's double-invoked dev effects.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    if (!isHiveMode()) {
      setError('Hive sign-in is not configured (VITE_HIVE_URL is unset).');
      return;
    }

    handleCallback()
      .then((returnTo) => {
        // returnTo came from our own sessionStorage txn — same-app path only.
        window.location.replace(returnTo.startsWith('/') ? returnTo : '/app');
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      });
  }, []);

  if (error) {
    return (
      <div className="mx-auto flex min-h-[80vh] max-w-md items-center px-6">
        <Card className="w-full">
          <h1 className="text-2xl font-semibold">Sign-in failed</h1>
          <p className="mt-2 text-sm text-[var(--color-critical)]">{error}</p>
          <p className="mt-4 text-sm text-slate-500">
            <Link to="/" className="text-[var(--color-accent)] underline-offset-4 hover:underline">
              Back to home
            </Link>{' '}
            and try signing in again.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-md items-center px-6">
      <Card className="w-full">
        <h1 className="text-2xl font-semibold">Signing you in…</h1>
        <p className="mt-2 text-sm text-slate-500">Completing Hive single sign-on.</p>
      </Card>
    </div>
  );
}
